import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@shadcn/components/ui/button';
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/ui/dialog';
import { Checkbox } from '@shadcn/components/ui/checkbox';
import { Input } from '@shadcn/components/ui/input';
import { Label } from '@shadcn/components/ui/label';
import Select from '@shadcn/components/ui/react-select';
import superagent from 'superagent';
import { backendUrl } from '@helpers/consts';

const FilterModal = ({ locationsByRegion, filters, onFiltersChange, isOpen, onClose, locationOrder = {} }) => {
  const [tempFilters, setTempFilters] = useState(filters);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTempFilters(filters);
  }, [filters, isOpen]);

  
  const fetchProductsData = async () => {
    try {
      setLoading(true);
      const response = await superagent
        .get(`${backendUrl}/product/active-skus`)
        .query({
          oldCategory: true
        })
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json');

      if (response.body && Array.isArray(response.body)) {
        setProducts(response.body);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchProductsData();
    }
  }, [isOpen]);

  
  const productOptions = useMemo(() => {
  
    const productMap = new Map();
    products.forEach(item => {
      if (!productMap.has(item.productName)) {
        productMap.set(item.productName, {
          productName: item.productName,
          category: item.category,
          skus: [item.sku],
          variantNames: [item.variantName]
        });
      } else {
        const existing = productMap.get(item.productName);
        existing.skus.push(item.sku);
        existing.variantNames.push(item.variantName);
      }
    });

    return Array.from(productMap.values()).map(product => ({
      value: product.productName,
      label: `${product.productName} (${product.skus.length} variants)`,
      skus: product.skus,
      category: product.category,
      variantNames: product.variantNames
    }));
  }, [products]);

  const handleProductChange = (selectedProducts) => {
    const productNames = selectedProducts ? selectedProducts.map(s => s.value) : [];
    const skus = selectedProducts ? selectedProducts.flatMap(p => p.skus) : [];

    setTempFilters(prev => ({
      ...prev,
      productNames,
      skus
    }));
  };

  const handleRegionChange = (region, checked) => {
    
    const regionLocationNames = locationOrder && locationOrder[region] ? locationOrder[region] : [];
    let newSelectedLocations;

    if (checked) {
      newSelectedLocations = [
        ...new Set([...tempFilters.selectedLocations, ...regionLocationNames]),
      ];
    } else {
      newSelectedLocations = tempFilters.selectedLocations.filter(
        (loc) => !regionLocationNames.includes(loc)
      );
    }

    const newSelectedRegions = Object.keys(locationOrder).filter((r) => {
      const regionLocs = locationOrder[r] || [];
      return newSelectedLocations.some((loc) => regionLocs.includes(loc));
    });

    setTempFilters((prev) => ({
      ...prev,
      selectedLocations: newSelectedLocations,
      selectedRegions: newSelectedRegions,
    }));
  };

  const handleLocationChange = (locationName, checked) => {
    const newSelectedLocations = checked
      ? [...tempFilters.selectedLocations, locationName]
      : tempFilters.selectedLocations.filter((loc) => loc !== locationName);

    const newSelectedRegions = Object.keys(locationOrder).filter((region) => {
      const regionLocationNames = locationOrder[region] || [];
      return newSelectedLocations.some((loc) => regionLocationNames.includes(loc));
    });

    setTempFilters((prev) => ({
      ...prev,
      selectedLocations: newSelectedLocations,
      selectedRegions: newSelectedRegions,
    }));
  };

  const handleApply = () => {
    onFiltersChange(tempFilters);
    onClose();
  };

  return (
    <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
      <DialogHeader className="flex flex-row items-center justify-between">
        <DialogTitle>Filters</DialogTitle>
        <div className="flex gap-2">
          <Button onClick={handleApply} size="sm">
            Apply Filters
          </Button>
        </div>
      </DialogHeader>

      <div className="space-y-6 py-4">
        <div className="space-y-2 px-1">
          <Label htmlFor="search">Search</Label>
          <Input
            id="search"
            placeholder="Search products, SKUs..."
            value={tempFilters.searchTerm}
            onChange={(e) =>
              setTempFilters((prev) => ({ ...prev, searchTerm: e.target.value }))
            }
            className="w-full"
          />
        </div>

        {/* Product Filter */}
        <div className="space-y-2">
          <Label>Products</Label>
          <Select
            isMulti
            value={productOptions.filter(opt => tempFilters.productNames?.includes(opt.value))}
            onChange={handleProductChange}
            options={productOptions}
            placeholder="Select products..."
            className="text-sm"
            classNamePrefix="react-select"
            maxMenuHeight={200}
            isLoading={loading}
          />
          {tempFilters.productNames?.length > 0 && (
            <p className="text-xs text-gray-500">
              {tempFilters.productNames.length} product{tempFilters.productNames.length !== 1 ? 's' : ''} selected
              {tempFilters.skus?.length > 0 && ` (${tempFilters.skus.length} SKUs)`}
            </p>
          )}
        </div>

        <div className="space-y-4">
          <Label className="text-base font-medium px-1">Regions & Locations</Label>
          <div className="space-y-5 rounded-lg border p-4">
            {Object.keys(locationOrder).map((region) => {
              const regionLocationNames = locationOrder[region] || [];

              const selectedInRegion = tempFilters.selectedLocations.filter((l) =>
                regionLocationNames.includes(l)
              );
              const isAllSelected =
                regionLocationNames.length > 0 &&
                selectedInRegion.length === regionLocationNames.length;
              const isPartiallySelected =
                selectedInRegion.length > 0 && !isAllSelected;

              return (
                <div key={region}>
                  <div className="flex items-center mb-3">
                    <Checkbox
                      id={`region-${region}`}
                      checked={isAllSelected}
                      data-state={
                        isPartiallySelected
                          ? 'indeterminate'
                          : isAllSelected
                          ? 'checked'
                          : 'unchecked'
                      }
                      onCheckedChange={(checked) => handleRegionChange(region, checked)}
                    />
                    <Label
                      htmlFor={`region-${region}`}
                      className="ml-3 mb-0 text-md font-semibold text-gray-800">
                      {region}
                    </Label>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-2 pl-7">
                    {regionLocationNames.map((locationName) => (
                      <div key={locationName} className="flex items-center">
                        <Checkbox
                          id={`location-${locationName}`}
                          checked={tempFilters.selectedLocations.includes(locationName)}
                          onCheckedChange={(checked) =>
                            handleLocationChange(locationName, checked)
                          }
                        />
                        <Label
                          htmlFor={`location-${locationName}`}
                          className="ml-2 mb-0 text-sm font-normal truncate"
                          title={locationName}>
                          {locationName}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-3 px-1">
          <Label className="text-base font-medium mb-0">Status</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { value: 'preorder', label: 'Preorder' },
              { value: 'out of stock', label: 'Out of Stock' },
              { value: 'off site', label: 'Off Site' },
              { value: 'available', label: 'Available' },
            ].map((status) => (
              <div key={status.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`status-${status.value}`}
                  checked={tempFilters.statusFilter.includes(status.value)}
                  onCheckedChange={(checked) => {
                    const newStatusFilter = checked
                      ? [...tempFilters.statusFilter, status.value]
                      : tempFilters.statusFilter.filter((s) => s !== status.value);
                    setTempFilters((prev) => ({ ...prev, statusFilter: newStatusFilter }));
                  }}
                />
                <Label htmlFor={`status-${status.value}`} className="text-sm mb-0">
                  {status.label}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3 px-1">
          <Label className="text-base font-medium">Next Container</Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="has-container"
                checked={tempFilters.hasContainer}
                onCheckedChange={(checked) =>
                  setTempFilters((prev) => ({ ...prev, hasContainer: checked }))
                }
              />
              <Label htmlFor="has-container" className="mb-0">Has Upcoming Container</Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="container-from" >Container Date From</Label>
              <Input
                id="container-from"
                type="date"
                value={tempFilters.containerDateFrom}
                onChange={(e) =>
                  setTempFilters((prev) => ({ ...prev, containerDateFrom: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="container-to">Container Date To</Label>
              <Input
                id="container-to"
                type="date"
                value={tempFilters.containerDateTo}
                onChange={(e) =>
                  setTempFilters((prev) => ({ ...prev, containerDateTo: e.target.value }))
                }
              />
            </div>
          </div>
        </div>

        <div className="space-y-3 px-1">
          <Label className="text-base font-medium">Pending Stock</Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="has-pending"
                checked={tempFilters.hasPending}
                onCheckedChange={(checked) =>
                  setTempFilters((prev) => ({ ...prev, hasPending: checked }))
                }
              />
              <Label className="mb-0" htmlFor="has-pending">Has Pending Stock</Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pending-min">Min Pending</Label>
              <Input
                id="pending-min"
                type="number"
                min="0"
                value={tempFilters.pendingMin}
                onChange={(e) =>
                  setTempFilters((prev) => ({ ...prev, pendingMin: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pending-max">Max Pending</Label>
              <Input
                id="pending-max"
                type="number"
                min="0"
                value={tempFilters.pendingMax}
                onChange={(e) =>
                  setTempFilters((prev) => ({ ...prev, pendingMax: e.target.value }))
                }
              />
            </div>
          </div>
        </div>
      </div>
    </DialogContent>
  );
};

export default FilterModal;
